import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, MapPin, Calendar, TrendingUp, TrendingDown, Download, Users, FileText } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell } from "recharts";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value}`;
};

const COLORS = ['#2E7D32', '#C9A227', '#E65100', '#1976D2', '#7B1FA2'];

const AgentReports = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("sales");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [salesReport, setSalesReport] = useState(null);
  const [paymentReport, setPaymentReport] = useState(null);
  const [destinationReport, setDestinationReport] = useState(null);
  const [monthReport, setMonthReport] = useState(null);
  const [yearReport, setYearReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employeeSalesReport, setEmployeeSalesReport] = useState(null);
  const [empDatePreset, setEmpDatePreset] = useState("this_year");
  const [empFromDate, setEmpFromDate] = useState("");
  const [empToDate, setEmpToDate] = useState("");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  useEffect(() => {
    fetchReports();
  }, [activeTab, selectedYear, empDatePreset, empFromDate, empToDate]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (activeTab === "sales") {
        const res = await axios.get(`${API}/agent/reports/sales?year=${selectedYear}`, { headers });
        setSalesReport(res.data);
      } else if (activeTab === "payment") {
        const res = await axios.get(`${API}/agent/reports/payments?year=${selectedYear}`, { headers });
        setPaymentReport(res.data);
      } else if (activeTab === "destination") {
        const res = await axios.get(`${API}/agent/reports/destination?year=${selectedYear}`, { headers });
        setDestinationReport(res.data);
      } else if (activeTab === "month") {
        const res = await axios.get(`${API}/agent/reports/monthly?year=${selectedYear}`, { headers });
        setMonthReport(res.data);
      } else if (activeTab === "year") {
        const res = await axios.get(`${API}/agent/reports/yearly`, { headers });
        setYearReport(res.data);
      } else if (activeTab === "team") {
        let url = `${API}/agent/reports/employee-sales`;
        const params = new URLSearchParams();
        if (empDatePreset !== "custom") {
          params.append("preset", empDatePreset);
        } else {
          if (empFromDate) params.append("from_date", empFromDate);
          if (empToDate) params.append("to_date", empToDate);
        }
        const res = await axios.get(`${url}?${params.toString()}`, { headers });
        setEmployeeSalesReport(res.data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => headers.map(h => row[h]).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${selectedYear}.csv`;
    a.click();
  };

  const GrowthIndicator = ({ value }) => {
    if (!value || value === 0) return <span className="text-gray-500">-</span>;
    const isPositive = value > 0;
    return (
      <span className={`flex items-center gap-1 ${isPositive ? "text-green-400" : "text-red-400"}`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {Math.abs(value)}%
      </span>
    );
  };

  const exportTeamCSV = () => {
    if (!employeeSalesReport?.employees?.length) return;
    const rows = employeeSalesReport.employees.map(e => ({
      Name: e.name, Designation: e.designation || e.role, Quotes: e.total_quotes,
      Pending: e.pending_quotes, Approved: e.approved_quotes, Booked: e.booked_quotes,
      Lost: e.lost_quotes, Bookings: e.total_bookings, Pax: e.total_pax,
      "Quote Value": e.quote_value, "Booking Value": e.booking_value, "Conversion %": e.conversion_rate
    }));
    exportToCSV(rows, "employee_sales_report");
  };

  const exportTeamPDF = () => {
    if (!employeeSalesReport?.employees?.length) return;
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape
    const pw = 297, mx = 15;
    let y = 15;
    const teal = [16, 185, 129];
    const dark = [33, 33, 33];
    const grey = [120, 120, 120];

    // Title
    doc.setFontSize(18); doc.setTextColor(...dark); doc.setFont('helvetica', 'bold');
    doc.text('Employee Sales Report', mx, y); y += 6;
    doc.setFontSize(9); doc.setTextColor(...grey); doc.setFont('helvetica', 'normal');
    const _fmtDMY = (s) => { if (!s) return ''; const d = new Date(s); if (isNaN(d)) return s; return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; };
    doc.text(`Period: ${_fmtDMY(employeeSalesReport.from_date)} - ${_fmtDMY(employeeSalesReport.to_date)}`, mx, y);
    y += 3;
    doc.setDrawColor(...teal); doc.setLineWidth(0.8); doc.line(mx, y, pw - mx, y);
    y += 8;

    // Summary
    const smry = employeeSalesReport.summary;
    doc.setFillColor(245, 245, 245); doc.roundedRect(mx, y, pw - 2 * mx, 14, 2, 2, 'F');
    doc.setFontSize(8); doc.setTextColor(...grey);
    const sLabels = ['Team Members', 'Total Quotes', 'Bookings', 'Revenue', 'Conversion'];
    const sValues = [smry.total_team_members, smry.total_quotes, smry.total_bookings, formatCurrency(smry.total_booking_value || 0), `${smry.overall_conversion}%`];
    const sW = (pw - 2 * mx) / 5;
    sLabels.forEach((l, i) => {
      const sx = mx + i * sW + 5;
      doc.text(l, sx, y + 5);
      doc.setFontSize(11); doc.setTextColor(...dark); doc.setFont('helvetica', 'bold');
      doc.text(String(sValues[i]), sx, y + 10);
      doc.setFontSize(8); doc.setTextColor(...grey); doc.setFont('helvetica', 'normal');
    });
    y += 20;

    // Table
    const cols = ['Employee', 'Quotes', 'Pending', 'Approved', 'Booked', 'Lost', 'Bookings', 'Pax', 'Quote Value', 'Booking Value', 'Conv %'];
    const colW = [45, 18, 18, 20, 18, 16, 22, 16, 28, 28, 18];
    // Header
    doc.setFillColor(...teal); doc.rect(mx, y, pw - 2 * mx, 8, 'F');
    doc.setFontSize(7); doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold');
    let cx = mx + 2;
    cols.forEach((c, i) => { doc.text(c, cx, y + 5); cx += colW[i]; });
    y += 8;

    // Rows
    doc.setFont('helvetica', 'normal');
    employeeSalesReport.employees.forEach((emp, idx) => {
      if (y > 190) { doc.addPage(); y = 15; }
      if (idx % 2 === 0) { doc.setFillColor(250, 250, 250); doc.rect(mx, y, pw - 2 * mx, 7, 'F'); }
      doc.setFontSize(7); doc.setTextColor(...dark);
      cx = mx + 2;
      const vals = [emp.name, emp.total_quotes, emp.pending_quotes, emp.approved_quotes, emp.booked_quotes, emp.lost_quotes, emp.total_bookings, emp.total_pax, formatCurrency(emp.quote_value), formatCurrency(emp.booking_value), `${emp.conversion_rate}%`];
      vals.forEach((v, i) => { doc.text(String(v), cx, y + 5); cx += colW[i]; });
      y += 7;
    });

    // Footer
    doc.setFontSize(6); doc.setTextColor(...grey);
    doc.text(`Generated on ${_fmtDMY(new Date())}`, pw / 2, 205, { align: 'center' });

    doc.save(`Employee_Sales_Report_${employeeSalesReport.from_date}_${employeeSalesReport.to_date}.pdf`);
  };

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-reports">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">My Reports</h1>
          <p className="text-gray-500">View your business performance and analytics</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="bg-gray-50">
              <TabsTrigger value="sales" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4 mr-2" /> My Sales
              </TabsTrigger>
              <TabsTrigger value="payment" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                Payment Report
              </TabsTrigger>
              <TabsTrigger value="destination" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                Destination-wise
              </TabsTrigger>
              <TabsTrigger value="month" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                Monthly
              </TabsTrigger>
              <TabsTrigger value="year" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                Yearly
              </TabsTrigger>
              <TabsTrigger value="team" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Team Sales
              </TabsTrigger>
            </TabsList>

            {activeTab !== "year" && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32 bg-gray-50 border-gray-200 text-gray-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Sales Report - Agent's Own Performance */}
          <TabsContent value="sales" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-800">
                  My Sales Report - {selectedYear}
                  {salesReport?.agent_name && <span className="text-sm font-normal text-gray-500 ml-2">({salesReport.agent_name})</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !salesReport ? (
                  <p className="text-gray-500 text-center py-8">No sales data for {selectedYear}</p>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <p className="text-sm text-blue-600">Total Enquiries</p>
                        <p className="text-2xl font-bold text-blue-800">{salesReport.summary?.total_quotes || 0}</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                        <p className="text-sm text-amber-600">Approved</p>
                        <p className="text-2xl font-bold text-amber-800">{salesReport.summary?.approved_quotes || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                        <p className="text-sm text-green-600">Booked</p>
                        <p className="text-2xl font-bold text-green-800">{salesReport.summary?.booked_quotes || 0}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                        <p className="text-sm text-emerald-600">Revenue</p>
                        <p className="text-2xl font-bold text-emerald-800">{formatCurrency(salesReport.summary?.revenue || 0)}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                        <p className="text-sm text-purple-600">Conversion</p>
                        <p className="text-2xl font-bold text-purple-800">{salesReport.summary?.conversion_rate || 0}%</p>
                      </div>
                    </div>

                    {/* Status Breakdown */}
                    <div className="grid grid-cols-4 gap-3 mb-6">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">New</p>
                        <p className="text-lg font-semibold text-gray-700">{salesReport.summary?.new_quotes || 0}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Approved</p>
                        <p className="text-lg font-semibold text-green-600">{salesReport.summary?.approved_quotes || 0}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Booked</p>
                        <p className="text-lg font-semibold text-emerald-600">{salesReport.summary?.booked_quotes || 0}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Lost</p>
                        <p className="text-lg font-semibold text-red-500">{salesReport.summary?.lost_quotes || 0}</p>
                      </div>
                    </div>

                    {/* Monthly Chart */}
                    {salesReport.monthly && salesReport.monthly.length > 0 && (
                      <>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Monthly Performance</h4>
                        <div className="h-64 mb-6">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={salesReport.monthly}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                              <YAxis stroke="#64748b" fontSize={12} />
                              <Tooltip 
                                contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0" }}
                                formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name]}
                              />
                              <Legend />
                              <Bar dataKey="quotes" name="Enquiries" fill="#3b82f6" />
                              <Bar dataKey="booked" name="Booked" fill="#10b981" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Revenue Chart */}
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Monthly Revenue Trend</h4>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={salesReport.monthly}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                              <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip 
                                contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0" }}
                                formatter={(value) => [formatCurrency(value), "Revenue"]}
                              />
                              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981" }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Report */}
          <TabsContent value="payment" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Payment Report - {selectedYear}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(paymentReport?.data, "payment_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !paymentReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No payment data for {selectedYear}</p>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-50/50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Total Bookings</p>
                        <p className="text-2xl font-bold text-gray-900">{paymentReport.summary?.total_bookings || 0}</p>
                      </div>
                      <div className="bg-gray-50/50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Total Amount</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(paymentReport.summary?.total_amount || 0)}</p>
                      </div>
                      <div className="bg-gray-50/50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Total Paid</p>
                        <p className="text-2xl font-bold text-green-400">{formatCurrency(paymentReport.summary?.total_paid || 0)}</p>
                      </div>
                      <div className="bg-gray-50/50 p-4 rounded-lg">
                        <p className="text-gray-500 text-sm">Total Balance</p>
                        <p className="text-2xl font-bold text-red-400">{formatCurrency(paymentReport.summary?.total_balance || 0)}</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Booking ID</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Destination</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Amount</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Paid</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Balance</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {paymentReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3">
                                <span className="inline-block px-2 py-1 bg-secondary-500 text-gray-800 font-bold text-xs rounded">
                                  #{row.booking_ref}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-800">{row.destination}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded ${row.trip_type === 'international' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {row.trip_type === 'international' ? 'International' : 'Domestic'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(row.total_amount)}</td>
                              <td className="px-4 py-3 text-right text-green-400">{formatCurrency(row.amount_paid)}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={row.balance > 0 ? 'text-red-400' : 'text-green-400'}>
                                  {row.balance > 0 ? formatCurrency(row.balance) : 'Paid'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  row.payment_status === 'full' ? 'bg-emerald-100 text-emerald-700' :
                                  row.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {row.payment_status === 'full' ? 'Paid' : row.payment_status === 'partial' ? 'Partial' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Destination Report */}
          <TabsContent value="destination" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Destination Report (Domestic/International) - {selectedYear}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(destinationReport?.data, "destination_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !destinationReport?.data ? (
                  <p className="text-gray-500 text-center py-8">No data for {selectedYear}</p>
                ) : (
                  <>
                    {/* Domestic vs International Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-emerald-50 border border-green-500/30 p-6 rounded-lg">
                        <h3 className="text-green-400 font-semibold mb-4">Domestic</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-500 text-sm">Quotes</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.domestic?.quotes || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Bookings</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.domestic?.bookings || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Revenue</p>
                            <p className="text-xl font-bold text-green-400">{formatCurrency(destinationReport.summary?.domestic?.revenue || 0)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Pax</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.domestic?.pax || 0}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-500/30 p-6 rounded-lg">
                        <h3 className="text-blue-400 font-semibold mb-4">International</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-500 text-sm">Quotes</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.international?.quotes || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Bookings</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.international?.bookings || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Revenue</p>
                            <p className="text-xl font-bold text-blue-400">{formatCurrency(destinationReport.summary?.international?.revenue || 0)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Pax</p>
                            <p className="text-xl font-bold text-gray-800">{destinationReport.summary?.international?.pax || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pie Chart */}
                    {(destinationReport.summary?.domestic?.revenue > 0 || destinationReport.summary?.international?.revenue > 0) && (
                      <div className="h-64 mb-6">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Domestic', value: destinationReport.summary?.domestic?.revenue || 0 },
                                { name: 'International', value: destinationReport.summary?.international?.revenue || 0 }
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              <Cell fill="#2E7D32" />
                              <Cell fill="#1976D2" />
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Destination Details Table */}
                    {destinationReport.destinations?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Destination</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Pax</th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {destinationReport.destinations.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50/30">
                                <td className="px-4 py-3 text-gray-800 font-medium">{row.destination}</td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs px-2 py-1 rounded ${row.trip_type === 'international' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {row.trip_type === 'international' ? 'International' : 'Domestic'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-600">{row.quotes}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{row.bookings}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{row.pax}</td>
                                <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Report */}
          <TabsContent value="month" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Monthly Report - {selectedYear}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(monthReport?.data, "monthly_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !monthReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data for {selectedYear}</p>
                ) : (
                  <>
                    <div className="h-72 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthReport.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="month_name" stroke="#94a3b8" fontSize={12} />
                          <YAxis stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                          />
                          <Legend />
                          <Bar dataKey="quotes" name="Quotes" fill="#2E7D32" />
                          <Bar dataKey="bookings" name="Bookings" fill="#C9A227" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Month</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Pax</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {monthReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800">{row.month_name} {selectedYear}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.bookings}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.pax}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50/30">
                          <tr>
                            <td className="px-4 py-3 text-gray-800 font-semibold">Total</td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.quotes, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.bookings, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.pax, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-emerald-600 font-bold">
                              {formatCurrency(monthReport.data.reduce((s, r) => s + r.revenue, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Yearly Report */}
          <TabsContent value="year" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Year-on-Year Performance</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(yearReport?.data, "yearly_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !yearReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No yearly data available</p>
                ) : (
                  <>
                    <div className="h-72 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={yearReport.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                          <YAxis stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="quotes" name="Quotes" stroke="#2E7D32" strokeWidth={2} />
                          <Line type="monotone" dataKey="bookings" name="Bookings" stroke="#C9A227" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Year</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Pax</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Growth</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {yearReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800 font-semibold">{row.year}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.bookings}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.pax}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                              <td className="px-4 py-3 text-right">
                                <GrowthIndicator value={row.revenue_growth} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team / Employee Sales Report */}
          <TabsContent value="team" className="space-y-6" data-testid="team-sales-tab">
            {/* Date Filters */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-5">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Period</Label>
                    <Select value={empDatePreset} onValueChange={(v) => setEmpDatePreset(v)}>
                      <SelectTrigger className="w-44 bg-gray-50 border-gray-200 text-gray-800" data-testid="team-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                        <SelectItem value="this_quarter">This Quarter</SelectItem>
                        <SelectItem value="this_year">This Year</SelectItem>
                        <SelectItem value="custom">Custom Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {empDatePreset === "custom" && (
                    <>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">From</Label>
                        <Input type="date" value={empFromDate} onChange={e => setEmpFromDate(e.target.value)} className="bg-gray-50 border-gray-200 w-40" data-testid="team-from-date" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">To</Label>
                        <Input type="date" value={empToDate} onChange={e => setEmpToDate(e.target.value)} className="bg-gray-50 border-gray-200 w-40" data-testid="team-to-date" />
                      </div>
                    </>
                  )}
                  <div className="flex gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={() => exportTeamCSV()} className="border-gray-200 text-gray-600" data-testid="team-export-csv">
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportTeamPDF()} className="border-gray-200 text-gray-600" data-testid="team-export-pdf">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
                {employeeSalesReport && (() => {
                  const _fmt = (s) => { if (!s) return ''; const d = new Date(s); if (isNaN(d)) return s; return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; };
                  return (
                    <p className="text-xs text-gray-400 mt-2">
                      Showing data from {_fmt(employeeSalesReport.from_date)} to {_fmt(employeeSalesReport.to_date)}
                    </p>
                  );
                })()}
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
            ) : !employeeSalesReport ? (
              <Card className="bg-white border-gray-200"><CardContent className="py-12"><p className="text-gray-500 text-center">No data available</p></CardContent></Card>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="team-summary-cards">
                  <Card className="bg-blue-50 border-blue-100 shadow-sm">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-blue-600 font-medium">Team Members</p>
                      <p className="text-2xl font-bold text-blue-800">{employeeSalesReport.summary?.total_team_members || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-100 shadow-sm">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-amber-600 font-medium">Total Quotes</p>
                      <p className="text-2xl font-bold text-amber-800">{employeeSalesReport.summary?.total_quotes || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-50 border-emerald-100 shadow-sm">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-emerald-600 font-medium">Total Bookings</p>
                      <p className="text-2xl font-bold text-emerald-800">{employeeSalesReport.summary?.total_bookings || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-100 shadow-sm">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-green-600 font-medium">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-800">{formatCurrency(employeeSalesReport.summary?.total_booking_value || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-100 shadow-sm">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-xs text-purple-600 font-medium">Conversion Rate</p>
                      <p className="text-2xl font-bold text-purple-800">{employeeSalesReport.summary?.overall_conversion || 0}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Bar Chart - Employee Comparison */}
                {employeeSalesReport.employees?.length > 0 && (
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-gray-800 text-base">Employee Performance Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-72" data-testid="team-bar-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={employeeSalesReport.employees.filter(e => e.id !== '_unassigned')} barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" stroke="#64748b" fontSize={11} tick={{ fill: '#475569' }} />
                            <YAxis stroke="#64748b" fontSize={11} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }}
                              formatter={(value, name) => [name === 'Booking Value' ? formatCurrency(value) : value, name]}
                            />
                            <Legend />
                            <Bar dataKey="total_quotes" name="Quotes" fill="#3b82f6" radius={[4,4,0,0]} />
                            <Bar dataKey="booked_quotes" name="Booked" fill="#10b981" radius={[4,4,0,0]} />
                            <Bar dataKey="total_bookings" name="Bookings" fill="#f59e0b" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Revenue Comparison Chart */}
                {employeeSalesReport.employees?.filter(e => e.booking_value > 0).length > 0 && (
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-gray-800 text-base">Revenue by Employee</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64" data-testid="team-revenue-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={employeeSalesReport.employees.filter(e => e.id !== '_unassigned' && e.booking_value > 0)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={v => formatCurrency(v)} />
                            <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={11} width={100} />
                            <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }} />
                            <Bar dataKey="booking_value" name="Booking Value" fill="#10b981" radius={[0,4,4,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Detailed Table */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-800 text-base">Employee-wise Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto" data-testid="team-sales-table">
                      <table className="w-full">
                        <thead className="bg-gray-50/80">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employee</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Quotes</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Pending</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Approved</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Booked</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Lost</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Bookings</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Pax</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Quote Value</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Booking Value</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Conversion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {employeeSalesReport.employees?.map((emp) => (
                            <tr key={emp.id} className="hover:bg-gray-50/50" data-testid={`team-row-${emp.id}`}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-semibold text-gray-800">{emp.name}</p>
                                <p className="text-xs text-gray-400">{emp.designation || emp.role}</p>
                              </td>
                              <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-gray-700">{emp.total_quotes}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm text-amber-600">{emp.pending_quotes}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm text-blue-600">{emp.approved_quotes}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-emerald-600">{emp.booked_quotes}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm text-red-500">{emp.lost_quotes}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-gray-700">{emp.total_bookings}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm text-gray-600">{emp.total_pax}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm text-gray-600">{formatCurrency(emp.quote_value)}</span></td>
                              <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-emerald-600">{formatCurrency(emp.booking_value)}</span></td>
                              <td className="px-3 py-3 text-right">
                                <Badge className={emp.conversion_rate >= 50 ? 'bg-emerald-100 text-emerald-700' : emp.conversion_rate >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>
                                  {emp.conversion_rate}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {employeeSalesReport.employees?.length > 1 && (
                          <tfoot className="bg-gray-50/80 border-t-2 border-gray-200">
                            <tr>
                              <td className="px-4 py-3 text-sm font-bold text-gray-800">TOTAL</td>
                              <td className="px-3 py-3 text-right text-sm font-bold text-gray-800">{employeeSalesReport.summary?.total_quotes}</td>
                              <td className="px-3 py-3 text-right text-sm text-amber-600">{employeeSalesReport.employees?.reduce((s,e) => s + e.pending_quotes, 0)}</td>
                              <td className="px-3 py-3 text-right text-sm text-blue-600">{employeeSalesReport.employees?.reduce((s,e) => s + e.approved_quotes, 0)}</td>
                              <td className="px-3 py-3 text-right text-sm font-bold text-emerald-600">{employeeSalesReport.employees?.reduce((s,e) => s + e.booked_quotes, 0)}</td>
                              <td className="px-3 py-3 text-right text-sm text-red-500">{employeeSalesReport.employees?.reduce((s,e) => s + e.lost_quotes, 0)}</td>
                              <td className="px-3 py-3 text-right text-sm font-bold text-gray-800">{employeeSalesReport.summary?.total_bookings}</td>
                              <td className="px-3 py-3 text-right text-sm text-gray-600">{employeeSalesReport.employees?.reduce((s,e) => s + e.total_pax, 0)}</td>
                              <td className="px-3 py-3 text-right text-sm font-bold text-gray-600">{formatCurrency(employeeSalesReport.summary?.total_quote_value || 0)}</td>
                              <td className="px-3 py-3 text-right text-sm font-bold text-emerald-600">{formatCurrency(employeeSalesReport.summary?.total_booking_value || 0)}</td>
                              <td className="px-3 py-3 text-right">
                                <Badge className="bg-purple-100 text-purple-700">{employeeSalesReport.summary?.overall_conversion}%</Badge>
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AgentReports;
